import { Col, Row, Space } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import styled from 'styled-components';
import {
  JiraApi,
  ProjectMappingMechanism,
  SettingsMeta,
} from 'api/integrations';
import { ReactComponent as JiraIcon } from 'assets/svgs/jira-logo.svg';
import { PageLoading } from 'components/atoms/loading';
import { MaskableContent } from 'components/atoms/maskable-content';
import { Modal } from 'components/atoms/modal';
import { Page } from 'components/molecules/page';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import { GET_JIRA_CONFIG, useQueryJiraConfig } from 'queries/jira-integration';
import { logger } from 'services';
import { IntegrationHeader, PageContent } from '../../components';
import { IntegrationStatusTypes } from '../authentication-types';
import { AuthenticateJira } from './authenticate/authenticate';
import { CommentsSync } from './comments-sync/comments-sync';
import { EpicMapping } from './epic-mapping/epic-mapping';
import { FieldSettings } from './field-settings/field-settings';
import { ProjectSelection } from './project-selection/project-selection';
import { JiraSettingsPrompt } from './prompt';
import { StatusFieldSync } from './status-field-sync/status-field-sync';
import { SectionKeys } from './types';

const errorStrings = {
  onGetConfig: 'Integration/Jira/get-config',
  onUpdateConfig: 'Integration/Jira/update-config',
  onDisconnect: 'Integration/Jira/disconnect',
  invalidDisconnect: 'Integration/Jira/invalid-disconnect',
};

const Container = styled.div`
  padding-bottom: var(--spacing-12);
`;

export const Jira = () => {
  const pageTitle = useTranslation('settings.integrations.jira.title');
  const pageDescription = useTranslation(
    'settings.integrations.jira.description',
  );
  const changesSavedText = useTranslation('success');
  const changesSavedMessage = useTranslation('changes_saved.extra');
  const changesErrorText = useTranslation('failure');
  const changesErrorMessage = useTranslation('rte.error');
  const confirmTitle = useTranslation(
    'settings.integrations.jira.disconnect.title',
  );
  const confirmContent = useTranslation(
    'settings.integrations.jira.disconnect.content',
  );
  const disconnectText = useTranslation('disconnect');

  const queryClient = useQueryClient();
  const [authenticateUrl, setAuthenticateUrl] = useState('');
  const [reAuthUrl, setReAuthUrl] = useState('');

  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [updating, setUpdating] = useState<SectionKeys>();
  const [loadingAuth, setLoadingAuth] = useState(false);

  const getAuthDetails = useCallback(() => {
    setLoadingAuth(true);
    JiraApi.getJiraIntegrationProvider()
      .then((resp) => {
        setAuthenticateUrl(resp.oauthUrl);
        setReAuthUrl(resp.oauthReauthUrl);
      })
      .finally(() => setLoadingAuth(false));
  }, [setAuthenticateUrl, setLoadingAuth]);

  /**
   * #1 load jira config,
   * - if config is empty, it is un-authenticated, then fetch auth info
   */
  const {
    data: jiraConfig = [],
    isLoading: isLoadingConfig,
    isFetching: isFetchingConfig,
  } = useQueryJiraConfig({
    refetchOnWindowFocus: false,
    onSuccess: (res) => {
      // happens only if not authenticated
      if (res != null && res.length === 0) {
        getAuthDetails();
      }
    },
  });

  const isFixedMapping =
    jiraConfig[0]?.settingsMeta?.projectMappingMechanism ===
    ProjectMappingMechanism.Fixed;

  const updateJiraSettingsMutation = useMutation(
    ({
      integrationId,
      updatedConfig,
    }: {
      integrationId: number;
      updatedConfig: SettingsMeta;
    }) => JiraApi.updateJiraIntegration(integrationId, updatedConfig),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(GET_JIRA_CONFIG);

        return toastService({
          toastContainer: {},
          title: changesSavedText,
          message: changesSavedMessage,
        }).success();
      },
      onError: (error: any) => {
        queryClient.setQueryData(GET_JIRA_CONFIG, jiraConfig);

        logger.error(errorStrings.onUpdateConfig, { error: new Error(error) });

        return toastService({
          toastContainer: {},
          title: changesErrorText,
          message: changesErrorMessage,
        }).error();
      },
      onSettled: () => {
        setUpdating(SectionKeys.EMPTY);
      },
    },
  );

  const onDisconnect = () => {
    if (jiraConfig[0] != null) {
      setIsDisconnecting(true);
      JiraApi.disconnectJiraAuthentication(jiraConfig[0].integrationId)
        .then(() => {
          queryClient.setQueryData(GET_JIRA_CONFIG, []);
          getAuthDetails();
        })
        .catch((error) => {
          logger.error(errorStrings.onDisconnect, { error });
        })
        .finally(() => {
          setIsDisconnecting(false);
          setShowDisconnectModal(false);
        });

      return true;
    }

    logger.warn(errorStrings.invalidDisconnect, { jiraConfig });

    return false;
  };

  const onChange = useCallback(
    (updatedConfig: SettingsMeta, key?: SectionKeys) => {
      if (jiraConfig[0] != null) {
        if (key) {
          setUpdating(key);
        }
        updateJiraSettingsMutation.mutate({
          integrationId: jiraConfig[0].integrationId,
          updatedConfig,
        });
      }
    },
    [jiraConfig, updateJiraSettingsMutation],
  );

  useEffect(() => {
    if (jiraConfig[0]?.integrationStatus === IntegrationStatusTypes.Expired) {
      getAuthDetails();
    }
  }, [jiraConfig, getAuthDetails]);

  return (
    <Page>
      <IntegrationHeader
        title={pageTitle}
        icon={<JiraIcon />}
        subText={pageDescription}
      />
      <PageContent>
        <Container>
          <JiraSettingsPrompt
            isDirty={isDirty}
            onReset={() => setIsDirty(false)}
          />
          <Row>
            <Col
              xl={{ span: 16, offset: 4 }}
              span={16}
              offset={4}
              lg={{ offset: 1 }}
            >
              {isLoadingConfig === true ? <PageLoading /> : null}

              {isLoadingConfig === false ? (
                <Space direction="vertical" size={32} style={{ width: '100%' }}>
                  <AuthenticateJira
                    reAuthUrl={reAuthUrl}
                    oauthUrl={authenticateUrl}
                    jiraConfig={jiraConfig[0]}
                    onDisconnectClick={() => setShowDisconnectModal(true)}
                    loading={loadingAuth}
                  />
                  {jiraConfig[0] != null && (
                    <MaskableContent
                      showMask={
                        jiraConfig[0]?.integrationStatus ===
                        IntegrationStatusTypes.Expired
                      }
                    >
                      <ProjectSelection
                        jiraConfig={jiraConfig[0]}
                        onChange={onChange}
                        updating={updating}
                      />
                      <>
                        {isFixedMapping ? (
                          <>
                            <EpicMapping
                              jiraConfig={jiraConfig[0]}
                              onChange={onChange}
                              onDirty={setIsDirty}
                              updating={updating}
                            />
                            <FieldSettings
                              isFetching={isFetchingConfig}
                              jiraConfig={jiraConfig[0]}
                              onChange={onChange}
                              updating={updating}
                            />
                          </>
                        ) : null}
                        <StatusFieldSync
                          jiraConfig={jiraConfig[0]}
                          onChange={onChange}
                          updating={updating}
                        />
                        <CommentsSync
                          jiraConfig={jiraConfig[0]}
                          onChange={onChange}
                          updating={updating}
                        />
                      </>
                    </MaskableContent>
                  )}
                </Space>
              ) : null}
            </Col>
          </Row>

          {jiraConfig ? (
            <Modal
              visible={showDisconnectModal}
              title={confirmTitle}
              confirmLoading={false}
              onCancel={() => setShowDisconnectModal(false)}
              onOk={onDisconnect}
              okText={disconnectText}
              okType="danger"
              okButtonProps={{ loading: isDisconnecting }}
              cancelButtonProps={{ disabled: isDisconnecting }}
              closable={!isDisconnecting}
            >
              <p>{confirmContent}</p>
            </Modal>
          ) : null}
        </Container>
      </PageContent>
    </Page>
  );
};
